import { defineStore } from "pinia";
import { computed, nextTick, reactive, watch } from "vue";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

import { StorageHelper } from "@/utils/StorageHelper";
import { StorageName } from "@/utils/StorageHelper/constants";
import type { OutboxFilterParams } from "./types";
import type { MailLogItemsConditions } from "@/api/modules/autoQ/mailLogItems/types.ts";

const initialValues: OutboxFilterParams = {
    customer: undefined,
    creationDateFrom: "",
    creationDateTo: "",
};

export const useOutboxFilterParamsStore = defineStore("outboxFilterParamsStore", () => {
    const {
        write,
        read,
        remove
    } = new StorageHelper(StorageName.OutboxFilterParamsStore);

    const filterParams = reactive<OutboxFilterParams>(cloneDeep(initialValues));

    const changeValue = <Key extends keyof OutboxFilterParams>(
        propertyName: Key,
        value: OutboxFilterParams[Key]
    ) => {
        filterParams[propertyName] = value;
    };

    const hasActiveFilters = computed(() => !isEqual(initialValues, filterParams));

    // No URL query-param sync here: /mail-log-items/user always scopes to the logged-in user
    // server-side (from the JWT, not a client-sent condition), so a shared/deep-linked URL
    // can't show anyone else's items anyway - a persisted localStorage value (this user's own
    // last filter state) is the only persistence that's actually useful.
    const initialize = () => {
        const storedFilterParams = read<OutboxFilterParams>();

        Object.assign(filterParams, cloneDeep(initialValues));

        if (storedFilterParams) Object.assign(filterParams, storedFilterParams);
    };

    const reset = async () => {
        Object.assign(filterParams, cloneDeep(initialValues));

        await nextTick();

        remove();
    };

    watch(filterParams, () => {
        write(filterParams);
    }, { deep: true });

    // The 1:1 mapping onto MailLogItemsRequest's `conditions` contract - see
    // ElasticsearchMailLogItemParams::getRequestParam on the backend
    // (feature/LUV-2335-create-outbox-for-mail-log-items) for the field names this mirrors.
    // The request key is "dateRange", not "creationDate" - see MailLogItemsConditions.
    const conditions = computed<MailLogItemsConditions>(() => ({
        customer: filterParams.customer,
        dateRange: (filterParams.creationDateFrom || filterParams.creationDateTo) ? {
            from: filterParams.creationDateFrom || undefined,
            to: filterParams.creationDateTo || undefined,
        } : undefined,
    }));

    return {
        filterParams,
        changeValue,
        hasActiveFilters,
        initialize,
        reset,
        conditions,
    };
});
